import React, { memo, ChangeEvent, useState, useCallback } from 'react'
import { Select, ISelectOption } from 'components/forms/Selects'
import capitalize from 'lodash/capitalize'
import { TextArea } from 'transfix-ui/components/TextArea'
import { useDebouncedCallback } from 'use-debounce'
import { unitTypeOptions, UNIT_TYPE } from 'apps/fleet-planner/constants/loads'
import { IStop } from 'apps/fleet-planner/types/loads'
import { TextInput } from 'components/atoms'
import { Checkbox } from 'components/forms'
import { removeSpecialCharacters } from 'utils/sanitizeInputs'

interface IStopCommodity {
  unitQuantity?: string
  weight?: string
  commodity?: string
  unit?: string
}

// TODO Look into lodash matches
export const doesCommodityInfoMatchFirstStop = (
  firstStop: IStopCommodity,
  stopCommodity: IStopCommodity
) => {
  const { commodity, unit, unitQuantity, weight } = firstStop || {}

  const {
    commodity: commodityTwo,
    unit: unitTwo,
    unitQuantity: unitQuantityTwo,
    weight: weightTwo,
  } = stopCommodity || {}

  // Don't bother comparing if commodity info in the first stop is empty/undefined.
  if (!unitQuantity && !weight && !commodity && !unit) return false

  return (
    unitQuantity === unitQuantityTwo &&
    weight === weightTwo &&
    commodity === commodityTwo &&
    unit === unitTwo
  )
}
interface IStopDetailsProps {
  stop: Partial<IStop>
  onStopUpdate: (stop: Partial<IStop>) => void
  variant?: string
  firstPickup: Partial<IStop>
  id?: string
}

export const StopDetails = memo<IStopDetailsProps>(
  ({ stop, onStopUpdate, variant, firstPickup = {}, id }) => {
    const {
      appointmentNumber,
      bolNumber,
      commodity,
      instructions = '',
      poNumber,
      referenceNumber = '',
      stopSequence,
      stopType,
      unit,
      unitQuantity,
      weight,
    } = stop || {}

    const [debouncedInstructions, setDebonucedInstructions] = useState<string>(instructions)

    /**
     * Both `useDebounce` and `useDebouncedCallback` work with the `leading`
     * option. This param will execute the function once immediately when called.
     * Subsequent calls will be debounced until the timeout expires.
     * @see https://lodash.com/docs/4.17.15#debounce
     */
    const [debouncedOnStopUpdate] = useDebouncedCallback(
      () => onStopUpdate({ instructions: debouncedInstructions }),
      300
    )

    const {
      commodity: firstPickupCommodity,
      unit: firstPickupUnit,
      unitQuantity: firstPickupUnitQuantity,
      weight: firstPickupWeight,
    } = firstPickup || {}
    const isFirstPickup = stop.id ? stop.id === firstPickup.id : stop.uuid === firstPickup.uuid
    const isLoadDetails = variant === 'load-details'
    const [details, setDetails] = useState({ commodity, weight, unitQuantity, unit })
    const [isCommodityChecked, setIsCommodityChecked] = useState(
      doesCommodityInfoMatchFirstStop(firstPickup, details)
    )

    const changeCommodityCheckHandler = useCallback(() => {
      if (!isCommodityChecked) {
        setDetails({
          unitQuantity: firstPickupUnitQuantity,
          weight: firstPickupWeight,
          commodity: firstPickupCommodity,
          unit: firstPickupUnit,
        })
        onStopUpdate({
          unitQuantity: firstPickupUnitQuantity,
          weight: firstPickupWeight,
          commodity: firstPickupCommodity,
          unit: firstPickupUnit,
        })
      }
      setIsCommodityChecked(!isCommodityChecked)
    }, [
      firstPickupCommodity,
      firstPickupUnit,
      firstPickupUnitQuantity,
      firstPickupWeight,
      isCommodityChecked,
      onStopUpdate,
    ])

    const unitTypeChangeHandler = useCallback(
      (newUnitType: ISelectOption<UNIT_TYPE>) => {
        setDetails({
          ...details,
          unit: newUnitType.value,
        })
        onStopUpdate({ unit: newUnitType.value })
      },
      [onStopUpdate]
    )

    const onPoChangeInputHandler = useCallback(
      (inputChangeEvent: ChangeEvent<HTMLInputElement>) => {
        onStopUpdate({ poNumber: inputChangeEvent.target.value })
      },
      [onStopUpdate]
    )

    const onBolChangeInputHandler = useCallback(
      (inputChangeEvent: ChangeEvent<HTMLInputElement>) => {
        onStopUpdate({ bolNumber: inputChangeEvent.target.value })
      },
      [onStopUpdate]
    )

    const onAppointmentChangeInputHandler = useCallback(
      (inputChangeEvent: ChangeEvent<HTMLInputElement>) => {
        onStopUpdate({ appointmentNumber: inputChangeEvent.target.value })
      },
      [onStopUpdate]
    )
    const onReferenceNumberChangeInputHandler = useCallback(
      (inputChangeEvent: ChangeEvent<HTMLInputElement>) => {
        onStopUpdate({ referenceNumber: inputChangeEvent.target.value })
      },
      [onStopUpdate]
    )

    const onCommodityChangeInputHandler = useCallback(
      (inputChangeEvent: ChangeEvent<HTMLInputElement>) => {
        onStopUpdate({ commodity: inputChangeEvent.target.value })
      },
      [onStopUpdate]
    )

    const onWeightChangeInputHandler = useCallback(
      (inputChangeEvent: ChangeEvent<HTMLInputElement>) => {
        onStopUpdate({ weight: removeSpecialCharacters(inputChangeEvent.target.value) })
      },
      [onStopUpdate]
    )

    const onUnitQuantityChangeInputHandler = useCallback(
      (inputChangeEvent: ChangeEvent<HTMLInputElement>) => {
        onStopUpdate({ unitQuantity: inputChangeEvent.target.value })
      },
      [onStopUpdate]
    )

    const instructionsChangeHandler = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
        setDebonucedInstructions(value)
        debouncedOnStopUpdate()
      },
      [debouncedOnStopUpdate]
    )

    return (
      <>
        {!isFirstPickup && (
          <div className='cf mt2'>
            <div className={isLoadDetails ? 'col-md-12 pl0 mb4' : 'col-md-11 mb4'}>
              <Checkbox
                label='Same commodity, weight, and quantity as Pickup 1'
                checked={isCommodityChecked}
                onChange={changeCommodityCheckHandler}
                dataTest={`${id}-stop-details-commodity-check-${stopType}-${stopSequence}`}
              />
            </div>
          </div>
        )}
        <div className='cf'>
          <div className={isLoadDetails ? 'col-md-12 pl0' : 'col-md-11'}>
            <TextInput
              large
              disabled={isCommodityChecked && !isFirstPickup}
              label='Commodity'
              value={details?.commodity || ''}
              onChange={onCommodityChangeInputHandler}
              dataTest={`${id}-stop-details-commodity-input-${stopType}-${stopSequence}`}
            />
          </div>
        </div>
        <div className='cf mt2'>
          <div className={isLoadDetails ? 'col-md-4 pl0 pr0' : 'col-md-4'}>
            <TextInput
              large
              disabled={isCommodityChecked && !isFirstPickup}
              label='Weight (lbs)'
              value={details?.weight || ''}
              onChange={onWeightChangeInputHandler}
              dataTest={`${id}-stop-details-weight-input-${stopType}-${stopSequence}`}
            />
          </div>
          <div className={isLoadDetails ? 'col-md-4 pr0' : 'col-md-4'}>
            <TextInput
              large
              disabled={isCommodityChecked && !isFirstPickup}
              label='Quantity'
              value={details?.unitQuantity || ''}
              onChange={onUnitQuantityChangeInputHandler}
              dataTest={`${id}-stop-details-unity-quantity-input-${stopType}-${stopSequence}`}
            />
          </div>
          <div className={isLoadDetails ? 'col-md-4' : 'col-md-3'}>
            <Select
              large
              disabled={isCommodityChecked && !isFirstPickup}
              label='Unit Type'
              options={unitTypeOptions}
              value={details?.unit}
              onChange={unitTypeChangeHandler}
              dataTest={`${id}-stop-details-unit-type-input-${stopType}-${stopSequence}`}
            />
          </div>
        </div>
        <div className='cf mt2'>
          <div className={isLoadDetails ? 'col-md-12 pl0 mb4' : 'col-md-11 mb4'}>
            <TextInput
              large
              label='PO #'
              dataTest={`${id}-stop-details-po-input-${stopType}-${stopSequence}`}
              value={poNumber || ''}
              onChange={onPoChangeInputHandler}
            />
          </div>
        </div>
        <div className='cf mt2'>
          <div className={isLoadDetails ? 'col-md-12 pl0 mb4' : 'col-md-11 mb4'}>
            <TextInput
              large
              label='BOL #'
              dataTest={`${id}-stop-details-bol-input-${stopType}-${stopSequence}`}
              value={bolNumber || ''}
              onChange={onBolChangeInputHandler}
            />
          </div>
        </div>
        <div className='cf'>
          <div className={isLoadDetails ? 'col-md-12 pl0 mb4' : 'col-md-11 mb4'}>
            <TextInput
              large
              label='Appointment #'
              value={appointmentNumber || ''}
              onChange={onAppointmentChangeInputHandler}
              dataTest={`${id}-stop-details-appointment-input-${stopType}-${stopSequence}`}
            />
          </div>
        </div>
        <div className='cf'>
          <div className={isLoadDetails ? 'col-md-12 pl0 mb4' : 'col-md-11 mb4'}>
            <TextInput
              large
              label={`${capitalize(stop.stopType)} #`}
              value={referenceNumber || ''}
              onChange={onReferenceNumberChangeInputHandler}
              dataTest={`${id}-stop-details-reference-input-${stopType}-${stopSequence}`}
            />
          </div>
        </div>
        {!isLoadDetails && (
          <div className='cf'>
            <div className='col-md-11'>
              <TextArea
                ariaLabel={`${capitalize(stop.stopType)}-stop-details-instructions`}
                placeholder='Enter instructions for your driver'
                id={`${capitalize(stop.stopType)}-stop-details-instructions`}
                size='small'
                label={`${capitalize(stop.stopType)} instructions`}
                onChange={instructionsChangeHandler}
                value={debouncedInstructions}
              />
            </div>
          </div>
        )}
      </>
    )
  }
)
